import { useCallback, useEffect, useMemo, useState } from 'react';

import apisConfig from '@/config/apis.config';
import fetchWrapper from '@/services/fetchWrapper.service';

const API_BASE = `${apisConfig.apis.api.url}/art-games/color-mix`;
const LEADERBOARD_UPDATED_EVENT = 'color-mix:leaderboard-updated';

export const LEADERBOARD_LIMIT = 100;

export const STREAK_THRESHOLDS = [
  { min: 0, max: 10, threshold: 90, labelKey: 'streak_90' },
  { min: 11, max: 25, threshold: 93, labelKey: 'streak_93' },
  { min: 26, max: Infinity, threshold: 95, labelKey: 'streak_95' },
];

const clamp = (value, min, max) => Math.min(Math.max(value, min), max);

const resolveLeaderboardUsername = (entry) => entry?.displayName || 'Anonymous';

export const getThresholdBand = (streak) => {
  const normalized = Number.isFinite(streak) ? Math.max(0, Math.floor(streak)) : 0;
  return STREAK_THRESHOLDS.find((band) => normalized >= band.min && normalized <= band.max)
    ?? STREAK_THRESHOLDS[STREAK_THRESHOLDS.length - 1];
};

export const getStreakThreshold = (streak) => getThresholdBand(streak).threshold;

export const getStreakLabel = (streak) => getThresholdBand(streak).labelKey;

export const calculateRoundPoints = (match, threshold) => {
  const clampedMatch = clamp(match, 0, 100);
  if (clampedMatch < threshold) {
    return 0;
  }

  if (threshold >= 100) {
    return 20;
  }

  const basePoints = 20;
  const span = 100 - threshold;
  const raw = span <= 0 ? 0 : (24 * (clampedMatch - threshold)) / span;
  const bonus = clamp(Math.floor(raw), 0, 24);

  return basePoints + bonus;
};

export const evaluateRound = ({ match, poursUsed, streak, totalPoints, totalPours }) => {
  const band = getThresholdBand(streak);
  const threshold = band.threshold;
  const clampedMatch = clamp(match, 0, 100);
  const isCorrect = clampedMatch >= threshold;
  const roundPoints = isCorrect ? calculateRoundPoints(clampedMatch, threshold) : 0;
  const poursInteger = Math.max(0, Math.round(poursUsed));

  const nextStreak = isCorrect ? streak + 1 : 0;
  const updatedTotalPoints = totalPoints + roundPoints;
  const updatedTotalPours = totalPours + poursInteger;

  return {
    band,
    threshold,
    clampedMatch,
    isCorrect,
    roundPoints,
    nextStreak,
    updatedTotalPoints,
    updatedTotalPours,
    poursInteger,
  };
};

const normalizeLeaderboardEntry = (entry) => {
  const currentStreak = Number.isFinite(entry?.streak) ? entry.streak : 0;
  const bestStreakCandidate = Number.isFinite(entry?.bestStreak)
    ? entry.bestStreak
    : Number.isFinite(entry?.best)
      ? entry.best
      : undefined;
  const bestStreak = Number.isFinite(bestStreakCandidate)
    ? bestStreakCandidate
    : currentStreak;

  return {
    id: entry?.userId ?? entry?.id ?? entry?.uid ?? null,
    displayName: resolveLeaderboardUsername(entry),
    streak: bestStreak,
    bestStreak,
    currentStreak,
    totalPoints: entry?.totalPoints ?? entry?.points ?? 0,
    totalPours: entry?.totalPours ?? entry?.pours ?? 0,
    roundsPlayed: entry?.roundsPlayed ?? entry?.rounds ?? 0,
    displayRank: entry?.displayRank ?? entry?.rank ?? null,
    rank: entry?.rank ?? null,
  };
};

const sortEntries = (entries) => entries.slice().sort((a, b) => {
  if (b.streak !== a.streak) {
    return b.streak - a.streak;
  }

  if (b.totalPoints !== a.totalPoints) {
    return b.totalPoints - a.totalPoints;
  }

  if (a.totalPours !== b.totalPours) {
    return a.totalPours - b.totalPours;
  }

  return 0;
});

const dedupeEntries = (entries) => {
  const seen = new Set();
  return entries.filter((entry) => {
    const key = entry.id ?? `${entry.displayName}-${entry.rank}`;
    if (seen.has(key)) {
      return false;
    }

    seen.add(key);
    return true;
  });
};

const withRanks = (entries) => entries.map((entry, index) => ({
  ...entry,
  displayRank: index + 1,
}));

const buildLeaderboard = (entries) => withRanks(dedupeEntries(sortEntries(entries)));

const parseLeaderboardResponse = (data) => {
  if (!data) {
    return { top: [], user: null, neighbors: [] };
  }

  const topEntries = Array.isArray(data.top)
    ? data.top
    : Array.isArray(data.entries)
      ? data.entries
      : Array.isArray(data)
        ? data
        : [];

  const normalizedTop = topEntries.map(normalizeLeaderboardEntry).filter(Boolean).slice(0, LEADERBOARD_LIMIT);
  const top = buildLeaderboard(normalizedTop);

  const userEntry = data.user ? normalizeLeaderboardEntry(data.user) : null;
  const normalizedNeighbors = Array.isArray(data.neighbors)
    ? data.neighbors.map(normalizeLeaderboardEntry).filter(Boolean)
    : [];
  const neighbors = buildLeaderboard(normalizedNeighbors);

  let resolvedUser = null;
  if (userEntry) {
    const matchInTop = top.find((entry) => entry.id && userEntry.id && entry.id === userEntry.id);
    const matchInNeighbors = neighbors.find((entry) => entry.id && userEntry.id && entry.id === userEntry.id);
    const displayRank = matchInTop?.displayRank ?? matchInNeighbors?.displayRank ?? userEntry.displayRank ?? userEntry.rank ?? null;
    const rank = userEntry.rank ?? matchInTop?.rank ?? matchInNeighbors?.rank ?? null;

    resolvedUser = {
      ...userEntry,
      displayRank,
      rank,
    };
  }

  return {
    top,
    user: resolvedUser,
    neighbors,
  };
};

export const createRoundPayload = ({ match, poursUsed, roundPoints, roundId, userUsername }) => {
  return {
    displayName: userUsername || 'Anonymous',
    roundId,
    match: clamp(match, 0, 100),
    pours: Math.round(poursUsed),
    roundPoints,
  };
};

export const submitRoundResult = async (payload, options = {}) => {
  if (!payload) {
    return null;
  }

  const {
    emitUpdateEvent = true,
  } = options;

  const { response, data } = await fetchWrapper.post(`${API_BASE}/rounds`, {
    injectToken: true,
    body: payload,
  });

  if (!response.ok) {
    const message = data?.message || 'Unable to save Color Mix round.';
    throw new Error(message);
  }

  if (emitUpdateEvent && typeof window !== 'undefined') {
    window.dispatchEvent(new CustomEvent(LEADERBOARD_UPDATED_EVENT));
  }

  return data;
};

export const fetchLeaderboard = async () => {
  const { response, data } = await fetchWrapper.get(`${API_BASE}/leaderboard?limit=${LEADERBOARD_LIMIT}`, {
    injectToken: true,
  });

  if (!response.ok) {
    const message = data?.message || 'Unable to fetch Color Mix leaderboard.';
    throw new Error(message);
  }

  return parseLeaderboardResponse(data);
};

export const useColorMixLeaderboard = () => {
  const [entries, setEntries] = useState([]);
  const [userRow, setUserRow] = useState(null);
  const [neighborRows, setNeighborRows] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  const loadLeaderboard = useCallback(async () => {
    setIsLoading(true);
    try {
      const result = await fetchLeaderboard();
      setEntries(result.top);
      setUserRow(result.user);
      setNeighborRows(result.neighbors);
      setError(null);
    } catch (err) {
      setError(err);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    loadLeaderboard();
  }, [loadLeaderboard]);

  useEffect(() => {
    if (typeof window === 'undefined') {
      return undefined;
    }

    const handler = () => {
      loadLeaderboard();
    };

    window.addEventListener(LEADERBOARD_UPDATED_EVENT, handler);
    return () => window.removeEventListener(LEADERBOARD_UPDATED_EVENT, handler);
  }, [loadLeaderboard]);

  const leaderboardWithFallback = useMemo(() => {
    if (entries.length > 0) {
      return entries;
    }

    if (!userRow) {
      return [];
    }

    return buildLeaderboard([userRow, ...neighborRows]);
  }, [entries, neighborRows, userRow]);

  return {
    entries: leaderboardWithFallback,
    isLoading,
    error,
    userRow,
    neighborRows,
    refresh: loadLeaderboard,
  };
};

export default useColorMixLeaderboard;
